import { ICallGroups } from '@llws/lift-entity-interfaces';
import { IQueryCommandOptions } from '@llws/dynamic-router-utils';
import { isEqual } from 'lodash-es';
import { StoreBaseImplementationV2 } from '@/shared/classes/store-base-implementation-v2';
import { IAction, IStateStrictV2 } from '@/shared/interfaces/store-base';
import { adminRequest } from '@/shared/request-configs/admin-request';
import vm from '@/main';

// Base store configuration
const adminGatewayResourcePath: string = '/call-tracking-api/call_groups';
const listViewName: string = '';
const showLogging: boolean = false;
const defaultGetListFilters: IQueryCommandOptions = {
  alias: 'cg',
  relations: [{ path: 'cg.callGroupEntries', alias: 'cge' }],
  orderBy: [
    { field: 'cg.name', direction: 'asc' },
    { field: 'cge.orderBy', direction: 'asc' }],
  limit: 9999,
};
const defaultGetSingleFilters: IQueryCommandOptions = {};

// Instantiate the base store with the params
const base = new StoreBaseImplementationV2<ICallGroups>({
  adminGatewayResourcePath,
  defaultGetSingleFilters,
  defaultGetListFilters,
  listViewName,
  showLogging,
});

const callGroupsEntriesPath: string = '/call-tracking-api/call_group_entries';

interface ICallGroupGetters {
  list: ICallGroups[];
  editedCallGroups: ICallGroups[];
}
interface IStateStrictCallGroups<DataObject> extends IStateStrictV2<DataObject> {
  deletedEntryIds: number[],
}

export default {
  namespaced: base.namespaced,

  state: {
    ...base.state,
    deletedEntryIds: [] as number[],
  },

  getters: {
    ...base.getters,

    /**
     * Returns the full original Groups list with local edits applied on top of it.
     * Primary getter for UI state, always use this for UI you want reflecting user changes.
     * NEVER write or mutate the objects returned by this getter directly.
     * To update values, always use the 'setCallGroupEdited' mutation.
     */
    mergedCallGroups(state: IStateStrictCallGroups<ICallGroups>): ICallGroups[] {
      const data = state.list?.data || [];
      const { changes } = state;

      if (Object.keys(state.changes).length === 0) return data;

      // Apply changes to existing records
      const merged = data.map((group) => {
        const changesForId = changes[group.id ?? 0];
        return changesForId ? { ...group, ...changesForId } : group;
      });

      // Append brand new records (those with negative/temp IDs not in original data)
      const newGroupIds = Object.keys(changes).filter(
        (id) => !data.some((g) => g.id === Number(id)),
      );

      newGroupIds.forEach((id) => {
        merged.push({ id: Number(id), ...changes[id] } as ICallGroups);
      });

      return merged;
    },

    /** Returns only the Call Groups that were edited on the UI */
    editedCallGroups(state: IStateStrictCallGroups<ICallGroups>): ICallGroups[] {
      if (!state.list?.data) return [];

      return Object.entries(state.changes).map(([id, changes]) => {
        const original = (state.list?.data ?? []).find((v) => v.id === Number(id));
        // If no original found, it's a new group—just return the changes as the object
        return {
          ...(original || {}),
          ...changes,
        } as ICallGroups;
      });
    },
    /** Returns the call group associated with the AI agent entry. 
     * As of now, each client is expected to have a single AI agent call group. */
    aiVoiceAgentCallGroup(state: IStateStrictCallGroups<ICallGroups>): ICallGroups | undefined {
      const data = state.list?.data
      if (!data) return undefined;

      return data.filter((group) => {
        return group.callGroupEntries?.some((entry) => entry.isAiAgent)
      })[0] || undefined;
    }
  },

  mutations: {
    ...base.mutations,

    /** Resets both changes and deleted entries ids */
    resetChanges: (state: IStateStrictCallGroups<ICallGroups>) => {
      state.changes = {};
      state.deletedEntryIds = [];
    },

    /** Enhanced setEdited that supports list items by passing an ID */
    setCallGroupEdited(
      state: IStateStrictCallGroups<ICallGroups>,
      payload: { id: number; key: keyof ICallGroups; value: any },
    ) {
      const { id, key, value } = payload;

      // Find the original item in the list to check if we're reverting to original value
      const originalItem = state.list?.data?.find((group) => group.id === id);
      const originalValue = originalItem ? originalItem[key] : undefined;

      const isSameAsOriginal = isEqual(value, originalValue);
      const updatedChangesForId = { ...(state.changes[id] || {}) };

      if (isSameAsOriginal) {
        delete updatedChangesForId[key];
      } else {
        updatedChangesForId[key] = value;
      }

      // Update the changes object reactively
      if (Object.keys(updatedChangesForId).length === 0) {
        const allChanges = { ...state.changes };
        delete allChanges[id];
        state.changes = allChanges;
      } else {
        state.changes = {
          ...state.changes,
          [id]: updatedChangesForId,
        };
      }
    },

    /** Tracks the IDs of existing phone number entries removed by the user to be deleted during the next save. */
    addDeletedEntryId(state: IStateStrictCallGroups<ICallGroups>, id: number) {
      // If it's a real DB record (positive ID), track it for deletion
      if (id > 0) {
        state.deletedEntryIds.push(id);
      }
    },
  },

  actions: {
    ...base.actions,

    /**
     * Saves Call Groups and their nested entries using a multi-stage sync.
     * The process follows a strict execution order to maintain relational integrity:
     * 1. DELETIONS: Removes entries flagged in `state.deletedEntryIds` first.
     * 2. DIFFING & REORDERING: Compares entries edited state to original data and recalculates
     * `orderBy` based on current UI position.
     * 3. POST (Creation): Creates new Call Groups first to generate real IDs, then creates
     * new Entries using an `idMap` to resolve parent-child relationships.
     * 4. PUT (Updates): Performs bulk updates for existing Call Groups and Entries
     * that have modified fields or reordered positions.
     * 5. CLEANUP: Resets local change tracking and temporary ID maps after successful sync.
     */
    async saveEditedCallGroups(ctx: IAction<IStateStrictCallGroups<ICallGroups>>) {
      const { state, getters, commit } = ctx;
      const { list: originalData, editedCallGroups } = getters as ICallGroupGetters;
      const { deletedEntryIds } = state;

      if (!editedCallGroups?.length) return;

      // Handle call groups deletes
      if (deletedEntryIds.length > 0) {
        await adminRequest.delete(callGroupsEntriesPath, deletedEntryIds);
      }

      // Identify edited groups (excluding entries from the comparison)
      const groupsToSave = editedCallGroups
        .map(({ callGroupEntries, callGroupsHasInteractiveVoiceResponses, ...groupFields }) => groupFields)
        .filter((edited) => {
          const original = originalData.find((g) => g.id === edited.id);
          if (!original) return true; // New group

          const { callGroupEntries, ...originalFields } = original;
          return !isEqual(edited, originalFields);
        });

      // Identify reordered/edited entries
      const entriesToSave = editedCallGroups.flatMap((group) => {
        const originalEntries = originalData.find((g) => g.id === group.id)?.callGroupEntries || [];

        return (group.callGroupEntries || [])
          .map((entry, index) => ({ ...entry, orderBy: index + 1 })) // Map current array position to 'orderBy' + 1 (since index starts from 0)
          .filter((current) => {
            const original = originalEntries.find((o) => o.id === current.id);
            return !original || !isEqual(current, original);
          });
      });

      const groupsToCreate = groupsToSave.filter((g) => !g.id || g.id < 0);
      const entriesToCreate = entriesToSave.filter((e) => !e.id || e.id < 0);

      const groupsToUpdate = groupsToSave.filter((g) => g.id && g.id > 0);
      const entriesToUpdate = entriesToSave.filter((e) => e.id && e.id > 0);

      const idMap: Record<number, number> = {};

      if (groupsToCreate.length) {
        const clientId = vm().$rsGlobals.$currentClient.id;
        const groupsWithClientId = groupsToCreate.map((group) => ({ ...group, clientId }));

        const response = await adminRequest.post(adminGatewayResourcePath, groupsWithClientId);
        const savedNewGroups = response.data;

        // Map Temp IDs to Real IDs
        groupsToCreate.forEach((oldGroup, index) => {
          if (oldGroup.id && savedNewGroups[index]) {
            idMap[oldGroup.id] = savedNewGroups[index].id;
          }
        });
      }

      if (entriesToCreate.length) {
        const entriesWithResolvedIds = entriesToCreate.map((entry) => {
          // If the parent ID is negative, look it up in the map.
          // If it's positive, it's an existing group, so keep the ID as-is.
          const realGroupId = entry.callGroupId! < 0
            ? idMap[entry.callGroupId!]
            : entry.callGroupId;

          return {
            ...entry,
            callGroupId: realGroupId,
          };
        });

        await adminRequest.post(callGroupsEntriesPath, entriesWithResolvedIds);
      }

      if (groupsToUpdate.length) await adminRequest.put(adminGatewayResourcePath, groupsToUpdate);
      if (entriesToUpdate.length) await adminRequest.put(callGroupsEntriesPath, entriesToUpdate);

      commit('resetChanges');
    },
  },
};
